import type {Bilingue} from './langue';

/**
 * Les textes de l'interface, dans les deux langues.
 *
 * Une seule table, plate, par ordre d'apparition a l'ecran. Les cles sont en
 * francais parce que le projet l'est ; les valeurs se lisent cote a cote,
 * et l'on voit d'un coup d'oeil ce qui manque.
 *
 * Y ajouter une chaine avant de l'ecrire dans un composant : une chaine en
 * dur ne se traduit pas, et c'est ainsi que l'interface s'etait mise a
 * melanger les deux langues.
 */
export const T = {
    // Navigation
    tableauDeBord: {fr: 'Tableau de bord', en: 'Dashboard'},
    bugs: {fr: 'Bugs', en: 'Bugs'},
    membres: {fr: "Membres de l'équipe", en: 'Team members'},
    versions: {fr: 'Versions', en: 'App versions'},
    creer: {fr: 'Créer', en: 'Create'},
    seDeconnecter: {fr: 'Se déconnecter', en: 'Sign out'},

    // Colonnes de la liste
    colonneEtat: {fr: 'État', en: 'Status'},
    colonneSujet: {fr: 'Sujet', en: 'Topic'},
    colonneEquipe: {fr: 'Équipe', en: 'Team'},
    colonneEtiquettes: {fr: 'Étiquettes', en: 'Tags'},
    colonneMessage: {fr: 'Message', en: 'Message'},
    colonnePlateforme: {fr: 'Plateforme', en: 'Platform'},
    colonneDate: {fr: 'Date', en: 'Date'},
    colonneActions: {fr: 'Actions', en: 'Actions'},

    // Liste
    chargementDesDemandes: {fr: 'Chargement des demandes…', en: 'Loading feedback…'},
    aucuneDemande: {
        fr: 'Aucune demande ne correspond à ces filtres.',
        en: 'No feedback found matching your filters.',
    },
    enVoirPlus: {fr: 'En voir plus', en: 'Load more'},
    chargement: {fr: 'Chargement…', en: 'Loading…'},
    echecMiseAJour: {
        fr: "Certaines demandes n'ont pas pu être mises à jour. Réessayez.",
        en: 'Failed to update some items. Please try again.',
    },
    nouvelleReponseDuClient: {fr: 'Nouvelle réponse du client', en: 'New user reply'},
    versionPerimee: {fr: 'Version périmée', en: 'Outdated version'},
    voir: {fr: 'Voir', en: 'View'},

    // Filtres
    rechercher: {fr: 'Rechercher un message, une route…', en: 'Search messages, routes…'},
    retirerLeFiltre: {fr: 'Retirer le filtre utilisateur', en: 'Remove user filter'},
    sansChangement: {fr: '— Sans changement —', en: '— No change —'},
    toutesLesEquipes: {fr: 'Toutes les équipes', en: 'All teams'},

    // Fiche d'une demande
    retourAuTableauDeBord: {fr: 'Retour au tableau de bord', en: 'Back to dashboard'},
    supprimer: {fr: 'Supprimer', en: 'Delete'},
    message: {fr: 'Message', en: 'Message'},
    infosUtilisateur: {fr: 'Informations utilisateur', en: 'User info'},
    nomAffiche: {fr: 'Nom affiché', en: 'Display name'},
    adresseEmail: {fr: 'Adresse e-mail', en: 'Email'},
    verifiee: {fr: 'Vérifiée', en: 'Verified'},
    methodeDeclaree: {fr: 'Méthode déclarée', en: 'Declared method'},
    numeroDeCommande: {fr: 'Numéro de commande', en: 'Order number'},
    lieuAchat: {fr: "Lieu d'achat", en: 'Purchase channel'},
    nonPrecisee: {fr: 'non précisée', en: 'not specified'},
    // Fiche : blocs techniques
    identifiantUtilisateur: {fr: 'Identifiant utilisateur', en: 'User ID'},
    compteCree: {fr: 'Compte créé le', en: 'Account created'},
    derniereConnexion: {fr: 'Dernière connexion', en: 'Last sign in'},
    moyensDeConnexion: {fr: 'Moyens de connexion', en: 'Auth providers'},
    utilisateurIntrouvable: {
        fr: "Cet utilisateur n'existe plus dans Firebase Auth",
        en: 'User not found in Firebase Auth',
    },
    chargementUtilisateur: {fr: 'Chargement du compte…', en: 'Loading user info…'},
    chargementAchats: {fr: 'Chargement des achats…', en: 'Loading purchases…'},
    infosAppareil: {fr: "Informations sur l'appareil", en: 'Device info'},
    plateforme: {fr: 'Plateforme', en: 'Platform'},
    versionApplication: {fr: "Version de l'application", en: 'App version'},
    ecranOuvert: {fr: 'Écran ouvert', en: 'Current route'},
    langue: {fr: 'Langue', en: 'Locale'},
    modeBeta: {fr: 'Mode bêta', en: 'Beta mode'},
    capture: {fr: 'Capture', en: 'Screenshot'},
    nonRenseigne: {fr: 'non renseigné', en: 'not set'},
    achete: {fr: 'Acheté :', en: 'Purchased:'},
    versionLaPlusRecenteInconnue: {
        fr: 'Version la plus récente non configurée',
        en: 'Latest version not configured',
    },
    aJour: {fr: 'À jour', en: 'Up to date'},
    nonConfirme: {
        fr: 'non confirmé — mise à jour à vérifier',
        en: 'not confirmed — update to be checked',
    },

    // Fiche : suivi par l'equipe
    actionsEquipe: {fr: "Actions de l'équipe", en: 'Admin actions'},
    aucunResponsable: {fr: 'Aucun responsable', en: 'No assignee'},
    aucuneEquipe: {fr: 'Aucune équipe', en: 'No team assigned'},
    notesInternes: {
        fr: "Notes internes, à l'usage de l'équipe…",
        en: 'Add internal notes for the team…',
    },
    trouverLaCommande: {
        fr: 'Retrouver cette commande dans le back-office des ventes',
        en: 'Find this order in the sales back-office',
    },
    // Fiche : ce que la recherche automatique a trouve
    rechercheAutomatique: {fr: 'Recherche automatique', en: 'Automatic search'},
    rechercheEnEchec: {fr: 'recherche en échec', en: 'search failed'},
    completeLaDemande: {fr: 'Complète la demande', en: 'Completes the request'},
    canalDachatDeclare: {fr: "Canal d'achat déclaré", en: 'Declared purchase channel'},
    emailDeContact: {fr: 'E-mail de contact', en: 'Contact email'},
    objetDuMessage: {fr: "Objet de l'e-mail", en: 'Email subject'},
    origine: {fr: 'Origine', en: 'Source'},
    sujet: {fr: 'Sujet', en: 'Topic'},
    retrouveDepuisEmail: {fr: "Retrouvé depuis l'e-mail ?", en: 'Found from the email?'},
    emailAbsent: {
        fr: 'absent — la réponse ne partira pas',
        en: 'missing — the reply will not be sent',
    },
    // Audit des achats
    chargementAudit: {fr: "Chargement de l'audit…", en: 'Loading audit data…'},
    rechercheParEmailOuUid: {
        fr: 'Rechercher par e-mail ou identifiant…',
        en: 'Search by email or UID…',
    },
    colonneCommande: {fr: 'Commande', en: 'Order'},
    colonneJeton: {fr: 'Jeton', en: 'Token'},
    colonneAchete: {fr: 'Acheté', en: 'Purchased'},
    colonneDerniereUtilisation: {fr: 'Dernière utilisation', en: 'Last used'},
    colonneRevoque: {fr: 'Révoqué', en: 'Revoked'},
    colonneErreur: {fr: 'Erreur / détails', en: 'Error / details'},
    colonneAction: {fr: 'Action', en: 'Action'},
    confirmer: {fr: 'Confirmer ?', en: 'Confirm?'},
    oui: {fr: 'oui', en: 'yes'},
    supprimerLicences: {
        fr: 'Supprimer les licences frauduleuses',
        en: 'Delete fraudulent licenses',
    },
    desactiverLeCompte: {fr: 'Désactiver le compte', en: 'Disable account'},

    // Membres de l'equipe
    chargementMembres: {fr: "Chargement de l'équipe…", en: 'Loading team members…'},
    choisirUneEquipe: {fr: 'Choisir une équipe', en: 'Select a team'},
    supprimerPoint: {fr: 'Supprimer ?', en: 'Delete?'},
    modifier: {fr: 'Modifier', en: 'Edit'},

    // Conversation
    conversation: {fr: 'Conversation', en: 'Conversation'},
    messageDorigine: {fr: "Client (message d'origine)", en: 'User (original feedback)'},
    ecrireUneReponse: {
        fr: 'Écrivez votre réponse… (Ctrl+Entrée ou Cmd+Entrée pour envoyer)',
        en: 'Type your reply… (Ctrl+Enter or Cmd+Enter to send)',
    },
    annuler: {fr: 'Annuler', en: 'Cancel'},
    enregistrer: {fr: 'Enregistrer', en: 'Save'},
    messageEnvoye: {fr: 'Message envoyé', en: 'Message sent'},
    retirer: {fr: 'Retirer', en: 'Remove'},

    // Connexion
    titreConnexion: {fr: 'Back-office des retours', en: 'Feedback Admin'},
    sousTitreConnexion: {
        fr: 'Connectez-vous pour traiter les demandes',
        en: 'Sign in to manage user feedback',
    },

    // Ecrans divers
    chargementHistorique: {fr: "Chargement de l'historique…", en: 'Loading user history…'},
    aucuneAutreDemande: {
        fr: "Aucune autre demande de cette personne.",
        en: 'No other feedback from this user.',
    },
    chargementVersions: {fr: 'Chargement des versions…', en: 'Loading versions…'},
    supprimerDefinitivement: {
        fr: 'Supprimer définitivement cette demande',
        en: 'Delete this request permanently',
    },
    tous: {fr: 'Tous', en: 'All'},
    beta: {fr: 'Bêta', en: 'Beta'},
    filtreParUtilisateur: {fr: 'Filtré par utilisateur :', en: 'Filtering by user:'},

    // Barre de mise en forme
    gras: {fr: 'Gras', en: 'Bold'},
    italique: {fr: 'Italique', en: 'Italic'},
    barre: {fr: 'Barré', en: 'Strikethrough'},
    code: {fr: 'Code', en: 'Code'},

    // Analyse assistee
    analyseIA: {fr: 'Analyse assistée', en: 'AI analysis'},
    analyseEnCours: {fr: 'Analyse en cours…', en: 'Analysis in progress…'},
    demandesSimilaires: {fr: 'Demandes similaires', en: 'Similar feedback'},
    aucuneDemandeSimilaire: {
        fr: 'Aucune demande similaire trouvée',
        en: 'No similar feedbacks found',
    },
    confiance: {fr: 'Confiance :', en: 'Match confidence:'},
    // Filtres du tableau de bord
    rechercheLabel: {fr: 'Recherche', en: 'Search'},
    categorie: {fr: 'Catégorie', en: 'Category'},
    etiquette: {fr: 'Étiquette', en: 'Tag'},
    version: {fr: 'Version ≥', en: 'Version ≥'},
    responsable: {fr: 'Responsable', en: 'Assignee'},
    dateDebut: {fr: 'À partir du', en: 'From date'},
    dateFin: {fr: "Jusqu'au", en: 'To date'},
    reponsesNonLues: {fr: 'Réponses non lues', en: 'Unread replies'},
    stable: {fr: 'Stable', en: 'Stable'},
    toutesLesPlateformes: {fr: 'Toutes les plateformes', en: 'All platforms'},
    tousLesStatuts: {fr: 'Tous les statuts', en: 'All statuses'},
    // Bugs et synthese
    contenu: {fr: 'Contenu :', en: 'Content:'},
    contenuDernierEvenement: {
        fr: 'Contenu (dernier événement) :',
        en: 'Content (latest event):',
    },
    derniereOccurrence: {fr: 'Dernière occurrence :', en: 'Last occurrence:'},
    plateformes: {fr: 'Plateformes :', en: 'Platforms:'},
    versionsTouchees: {fr: 'Versions :', en: 'Versions:'},
    aucunBug: {fr: 'Aucun bug', en: 'No bug'},
    commentaire: {fr: 'Commentaire :', en: 'Comment:'},
    correctif: {fr: 'Correctif :', en: 'Fix:'},
    repondreAuSignalant: {fr: 'Répondre au signalant :', en: 'Reply to the reporter:'},
    aucunSignalantAPrevenir: {
        fr: 'aucun signalant à prévenir',
        en: 'no reporter to notify',
    },
    ouvrirLeSignalement: {
        fr: 'Ouvrir le signalement dans un nouvel onglet',
        en: 'Open the report in a new tab',
    },
    // Synthese des bugs
    analyseDesSignalements: {
        fr: 'Analyse des signalements des versions en cours…',
        en: 'Analyzing bug reports from current versions…',
    },
    lectureDuDepot: {fr: 'Lecture du dépôt…', en: 'Reading the repository…'},
    releveEnCours: {fr: 'Relevé en cours…', en: 'Collecting…'},
    aucunCandidat: {fr: 'Aucun candidat', en: 'No bug candidate'},
    publieSurSlack: {fr: 'Publié sur Slack', en: 'Posted to Slack'},
    marqueVerifie: {fr: 'Marqué vérifié', en: 'Tagged as verified'},
    verification: {fr: 'Vérification…', en: 'Checking…'},
    creation: {fr: 'Création…', en: 'Creating…'},
    marquage: {fr: 'Marquage…', en: 'Tagging…'},
    envoiEnCours: {fr: 'Envoi…', en: 'Sending…'},
    nouveauxSeulement: {fr: 'Nouveaux seulement', en: 'New only'},
    nouveauxTriesEnCours: {
        fr: 'Nouveaux + triés + en cours',
        en: 'New + triaged + in progress',
    },
    aucunSignalement: {fr: 'aucun signalement', en: 'no report'},
    derniereVersion: {fr: 'dernière version', en: 'latest version'},
    aucunIndiceSentry: {fr: 'aucun indice Sentry', en: 'no Sentry evidence'},
    origineAVerifier: {
        fr: 'origine du code à vérifier',
        en: 'code origin to be checked',
    },
    nePlusProposer: {
        fr: 'Ne plus proposer cette issue',
        en: 'Stop suggesting this issue',
    },
    // Fiche : achats et suivi
    achatsDuCompte: {fr: 'Achats du compte', en: 'User purchases'},
    verifierDansLeBackOffice: {
        fr: 'Vérifier les commandes dans le back-office ↗',
        en: 'Check orders in back-office ↗',
    },
    noteInterne: {fr: 'Note interne', en: 'Internal note'},
    autresDemandes: {
        fr: 'Autres demandes de cette personne',
        en: 'Other feedback from this user',
    },
    voirToutDansLaListe: {fr: 'Tout voir dans la liste', en: 'View all in list'},
    voirToutesSesDemandes: {fr: 'Voir toutes ses demandes', en: 'View all feedbacks'},
    envoyer: {fr: 'Envoyer', en: 'Send'},
    envoiEnCoursPoints: {fr: 'Envoi…', en: 'Sending…'},
    televersement: {fr: 'Téléversement…', en: 'Uploading…'},
    avertissementNotification: {
        fr: 'La personne recevra un e-mail dès que vous répondez.',
        en: 'The user will receive an email notification when you reply.',
    },
    // Actions communes
    appliquer: {fr: 'Appliquer', en: 'Apply'},
    application: {fr: 'Application…', en: 'Applying…'},
    enregistrerLesChangements: {fr: 'Enregistrer', en: 'Save changes'},
    enregistrement: {fr: 'Enregistrement…', en: 'Saving…'},
    seConnecter: {fr: 'Se connecter', en: 'Sign in'},
    connexionEnCours: {fr: 'Connexion…', en: 'Signing in…'},
    montrerLaCapture: {fr: 'Montrer la capture', en: 'Show screenshot'},
    masquerLaCapture: {fr: 'Masquer la capture', en: 'Hide screenshot'},
    joindreDesImages: {fr: 'Joindre des images', en: 'Attach images'},
    copier: {fr: 'Copier', en: 'Copy'},
    copie: {fr: 'Copié', en: 'Copied'},
    verifier: {fr: 'Vérifier', en: 'Verify'},
    reverifier: {fr: 'Revérifier', en: 'Re-check'},
    ajouterUnMembre: {fr: 'Ajouter un membre', en: 'Add new member'},
    modifierLeMembre: {fr: 'Modifier le membre', en: 'Edit member'},
    ajouter: {fr: 'Ajouter', en: 'Add'},
    mettreAJour: {fr: 'Mettre à jour', en: 'Update'},
} satisfies Record<string, Bilingue>;
